import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Navigate, Route, Routes } from 'react-router-dom';
import LoginPage from './views/pages/authentication/authentication3/Login3';
import Register from 'views/pages/authentication/authentication3/Register3';
import ForgotPassword from 'views/pages/authentication/authentication3/ForgotPassword3';
import ResetPassword from 'views/pages/authentication/authentication3/ResetPassword3';
import VerifyEmail from 'views/pages/authentication/authentication3/VerifyEmail3';
import Booking from 'views/pages/public/Booking';
import PageRoutes from 'routes';
import themes from 'themes';
import NavigationScroll from 'layout/NavigationScroll';
import { DateProvider } from 'contexts/DateContext';
import { useEffect, useState } from 'react';
import { resetSessionState } from 'views/services/api';

// ========================================
// FUNCIONES HELPER
// ========================================

// Función para decodificar JWT sin verificar
const decodeToken = (token) => {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (e) {
    return null;
  }
};

// Función para validar si el token es válido
const isTokenValid = (token) => {
  if (!token) return false;

  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return false;

  // Verificar si el token NO ha expirado
  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();

  return currentTime < expirationTime;
};

// Función para limpiar sesión
const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('hotelData');
  sessionStorage.removeItem('token');
  console.log('🧹 Sesión limpiada desde App.js');
};

// ==============================|| APP ||============================== //
const App = () => {
  const customization = useSelector((state) => state.customization);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // ========================================
  // VALIDAR SESIÓN AL CARGAR
  // ========================================
  useEffect(() => {
    const validateSession = () => {
      console.log('🔍 Validando sesión...');

      const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      const storedHotelData = JSON.parse(localStorage.getItem('hotelData') || 'null');

      // Caso 1: Hay token y es válido
      if (storedToken && isTokenValid(storedToken)) {
        if (storedUser?.role || storedHotelData) {
          console.log('✅ Sesión válida encontrada');
          setIsAuthenticated(true);
          setLoading(false);
          // Resetear estado de api.js por si acaso
          resetSessionState();
          return;
        }
      }

      // Caso 2: Token expirado o inválido
      if (storedToken && !isTokenValid(storedToken)) {
        console.log('⚠️ Token expirado detectado en App.js');
        clearSession();
        toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', {
          toastId: 'session-expired-app',
          autoClose: 3000
        });
      }

      // Caso 3: No hay sesión
      console.log('❌ No hay sesión válida');
      setIsAuthenticated(false);
      setLoading(false);
    };

    validateSession();
  }, []);

  // ========================================
  // MONITOREO DE CAMBIOS EN STORAGE (Sincronización entre tabs)
  // ========================================
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Si se elimina el token en otra tab, cerrar sesión aquí también
      if (e.key === 'token') {
        if (!e.newValue || e.newValue === null) {
          console.log('🔄 Token eliminado en otra tab, cerrando sesión...');
          setIsAuthenticated(false);
        } else if (e.newValue && isTokenValid(e.newValue)) {
          console.log('🔄 Token válido detectado en otra tab');
          setIsAuthenticated(true);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // ========================================
  // MONITOREO PERIÓDICO DE SESIÓN (cada 2 minutos)
  // ========================================
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSessionPeriodically = () => {
      const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!storedToken) {
        console.log('⚠️ Token no encontrado en verificación periódica');
        setIsAuthenticated(false);
        return;
      }

      if (!isTokenValid(storedToken)) {
        console.log('⚠️ Token expirado detectado en verificación periódica');
        clearSession();
        setIsAuthenticated(false);
        toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', {
          toastId: 'session-expired-periodic',
          autoClose: 3000
        });
      } else {
        console.log('✅ Token válido en verificación periódica');
      }
    };

    // Verificar cada 2 minutos
    const intervalId = setInterval(checkSessionPeriodically, 2 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  // ========================================
  // RENDERIZADO CONDICIONAL
  // ========================================

  // Mostrar loading mientras valida
  if (loading) {
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={themes(customization)}>
          <CssBaseline />
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              flexDirection: 'column',
              gap: '20px'
            }}
          >
            <div
              style={{
                width: '50px',
                height: '50px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
            <div style={{ fontSize: '1.2rem', color: '#666' }}>Cargando...</div>
          </div>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes(customization)}>
        <CssBaseline />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          limit={3}
        />
        <NavigationScroll>
          <DateProvider>
            {isAuthenticated ? (
              <PageRoutes />
            ) : (
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />
                <Route path="/reservas/:slug" element={<Booking />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            )}
          </DateProvider>
        </NavigationScroll>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
